import { DownloadOutlined } from '@ant-design/icons';
import { getActivityList, GetActivityListBody } from '@knowledge/service/api/activity';
import { Button, message, Select } from 'antd';
import { useExportFile } from 'components/ExportCenter/hooks/useExportCenter';
import { QuickRangePicker } from 'components/QuickRangePicker';
import dayjs from 'dayjs';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import { useRef, useState } from 'react';
import { activityGlobalExport, ActivityGlobalStatsBody } from 'services/api/dashboard';
import PlatformRegionSelector from './PlatformRegionSelector';
import styled from '@emotion/styled';
import { useActivityTypeLabel } from '@knowledge/Activity/share';
import { useDebounceEffect } from 'ahooks';
import { omit, uniq } from 'lodash-es';
import { useTranslate } from '@tolgee/react';
import isEqual from 'react-fast-compare';
import { last30days, last7days } from '../ImplementData/utils';

function Filter({
  defaultFilterValue,
  onFilterChange,
}: {
  defaultFilterValue: ActivityGlobalStatsBody;
  onFilterChange: (filter: ActivityGlobalStatsBody) => void;
}) {
  const { t } = useTranslate();

  const permisson = useGetPermission()('导出活动数据');

  const [value, setValue] = useState<
    ActivityGlobalStatsBody &
      Pick<GetActivityListBody, 'platformStoreScope' | 'activityTypes' | 'statuses'>
  >(defaultFilterValue);

  const preValueRef = useRef<typeof value>();

  const [searchedActivityIds, setSearchedActivityIds] = useState<number[]>([]);

  useDebounceEffect(
    () => {
      const preValue = preValueRef.current;

      const { platformStoreScope, activityTypes, statuses, activityIds, startTimeMs, endTimeMs } =
        value;

      Promise.resolve(
        preValue && isEqual(omit(preValue, 'activityIds'), omit(value, 'activityIds'))
          ? searchedActivityIds
          : getActivityList({
              platformStoreScope,
              activityTypes,
              statuses: statuses?.length ? statuses : ['HISTORY', 'PROGRESS', 'PLANNING'],
              activityFrom: dayjs(startTimeMs).format('YYYY-MM-DD HH:mm:ss'),
              activityTo: dayjs(endTimeMs).format('YYYY-MM-DD HH:mm:ss'),
              page: 1,
              pageSize: 9999,
              storeIds: [],
            }).then((res) => res.list.map(({ id }) => id)),
      )
        .then((searchedActivityIds) => {
          setSearchedActivityIds(searchedActivityIds);
          onFilterChange({
            startTimeMs,
            endTimeMs,
            activityIds: uniq([...searchedActivityIds, ...activityIds]),
          });
        })
        .catch(() => {
          message.error(t('phrase.获取数据失败，请稍后再试'));
        });
    },
    [value],
    { wait: 1000 },
  );

  const { platformStoreScope, activityTypes, statuses, startTimeMs, endTimeMs } = value;

  const platformRegionSelect = (
    <PlatformRegionSelector
      value={platformStoreScope}
      onChange={(platformStoreScope) =>
        setValue({
          ...value,
          platformStoreScope,
        })
      }
    />
  );

  const { activityTypeLabel } = useActivityTypeLabel();

  const typeSelect = (
    <Select
      maxTagCount="responsive"
      dropdownMatchSelectWidth={false}
      mode="multiple"
      placeholder={t('选择活动类型')}
      value={activityTypes}
      onChange={(activityTypes) =>
        setValue({
          ...value,
          activityTypes,
        })
      }
      options={Object.entries(activityTypeLabel).map(([value, label]) => ({
        value,
        label,
      }))}
    />
  );

  const statusSelect = (
    <Select
      maxTagCount="responsive"
      dropdownMatchSelectWidth={false}
      mode="multiple"
      placeholder={t('选择活动状态')}
      value={statuses}
      onChange={(statuses) =>
        setValue({
          ...value,
          statuses,
        })
      }
      options={[
        { value: 'HISTORY', label: t('yijieshu') },
        { value: 'PROGRESS', label: t('aide.进行中') },
        { value: 'PLANNING', label: t('planned') },
      ]}
    />
  );

  const rangeSelect = (
    <QuickRangePicker
      quickActions={[
        {
          key: 'last7days',
          label: t('remind.近x天', { x: 7 }),
          range: [last7days()[0].startOf('days'), last7days()[1].endOf('days')],
        },
        {
          key: 'last30days',
          label: t('remind.近x天', { x: 30 }),
          range: [last30days()[0].startOf('days'), last30days()[1].endOf('days')],
        },
      ]}
      value={[dayjs(startTimeMs), dayjs(endTimeMs)]}
      onChange={(range) => {
        const [start, end] = range || [];
        if (start && end) {
          setValue({
            ...value,
            startTimeMs: start.startOf('days').valueOf(),
            endTimeMs: end.endOf('days').valueOf(),
          });
        }
      }}
    />
  );

  const { run: onDownload, loading: downloading } = useExportFile();

  const exportButton = permisson && (
    <Button
      disabled={!searchedActivityIds.length}
      loading={downloading}
      icon={<DownloadOutlined />}
      onClick={() =>
        onDownload(() =>
          activityGlobalExport({
            activityIds: searchedActivityIds,
            startTimeMs,
            endTimeMs,
          }),
        )
      }
    />
  );

  return (
    <Container>
      <section>
        {platformRegionSelect}
        {typeSelect}
        {statusSelect}
      </section>
      <div>
        {exportButton}
        {rangeSelect}
      </div>
    </Container>
  );
}

const Container = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px !important;
  > section {
    display: flex;
    align-items: center;
    gap: 14px;
    flex: 1 0 0;
    max-width: 800px;
    > .ant-select {
      flex: 1 0 0;
    }
  }
  > div {
    flex: 0 0 auto;
    margin-left: 24px;
    > button {
      margin-right: 14px;
    }
  }
`;

export default Filter;
